import React, { useCallback, useRef, useState } from 'react';
import Modal from './Modal';
import { apiUrl, getApiBaseUrl } from '../services/api';

function toAbsoluteUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${getApiBaseUrl()}${url}`;
}

export default function LodgingImagesModal({ tripId, lodging, onClose, onImagesChanged }) {
  const [images, setImages] = useState(() =>
    Array.isArray(lodging?.images) ? [...lodging.images] : []
  );
  const [selected, setSelected] = useState(new Set());
  const [dragOverId, setDragOverId] = useState(null);
  const [fileDragOver, setFileDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dragSrcId = useRef(null);
  const fileInputRef = useRef(null);

  const lodgingId = lodging?.lodging_id;

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(images.map(i => i.image_id)));
  const clearSelection = () => setSelected(new Set());

  // ── Image-to-image drag reorder ──────────────────────────────────
  const onDragStart = (e, id) => {
    // Don't handle if files are being dragged from OS
    if (e.dataTransfer.types.includes('Files')) return;
    dragSrcId.current = id;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const onItemDragOver = (e, id) => {
    if (e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(id);
  };

  const onItemDrop = async (e, targetId) => {
    if (e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setDragOverId(null);
    const srcId = dragSrcId.current;
    if (!srcId || srcId === targetId) return;

    const next = [...images];
    const srcIdx = next.findIndex(i => i.image_id === srcId);
    const tgtIdx = next.findIndex(i => i.image_id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    setImages(next);

    await fetch(apiUrl(`/api/trips/${tripId}/lodgings/${lodgingId}/images/reorder`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: next.map(i => i.image_id) }),
    });
    onImagesChanged(next);
  };

  const onDragEnd = () => { dragSrcId.current = null; setDragOverId(null); };

  // ── Move up / down (arrows) ──────────────────────────────────────
  const move = async (idx, dir) => {
    const next = [...images];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setImages(next);
    await fetch(apiUrl(`/api/trips/${tripId}/lodgings/${lodgingId}/images/reorder`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: next.map(i => i.image_id) }),
    });
    onImagesChanged(next);
  };

  // ── Delete single ────────────────────────────────────────────────
  const deleteSingle = useCallback(async (imageId) => {
    await fetch(apiUrl(`/api/trips/${tripId}/lodgings/${lodgingId}/images/${imageId}`), {
      method: 'DELETE',
    });
    const next = images.filter(i => i.image_id !== imageId);
    setImages(next);
    setSelected(prev => { const s = new Set(prev); s.delete(imageId); return s; });
    onImagesChanged(next);
  }, [tripId, lodgingId, images, onImagesChanged]);

  // ── Delete selected ──────────────────────────────────────────────
  const deleteSelected = useCallback(async () => {
    if (!selected.size) return;
    const ids = [...selected];
    await fetch(apiUrl(`/api/trips/${tripId}/lodgings/${lodgingId}/images/bulk-delete`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_ids: ids }),
    });
    const next = images.filter(i => !selected.has(i.image_id));
    setImages(next);
    setSelected(new Set());
    onImagesChanged(next);
  }, [tripId, lodgingId, images, selected, onImagesChanged]);

  // ── Upload files ─────────────────────────────────────────────────
  const handleFiles = useCallback(async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let next = [...images];
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(apiUrl(`/api/trips/${tripId}/lodgings/${lodgingId}/image`), {
        method: 'POST',
        body,
      });
      if (!res.ok) continue;
      const payload = await res.json().catch(() => ({}));
      if (payload?.image_url) {
        next = [...next, { image_id: payload.image_id, media_url: payload.image_url }];
      }
    }
    setImages(next);
    setUploading(false);
    onImagesChanged(next);
  }, [tripId, lodgingId, images, onImagesChanged]);

  // ── File drop from OS file explorer ─────────────────────────────
  const onGridDragOver = (e) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setFileDragOver(true);
    }
  };

  const onGridDragLeave = (e) => {
    // Only clear if leaving the grid entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setFileDragOver(false);
    }
  };

  const onGridDrop = (e) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setFileDragOver(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const allSelected = images.length > 0 && selected.size === images.length;

  return (
    <Modal isOpen onClose={onClose} width="860px" cardClassName="tp-img-mgr-card">
      <div className="tp-img-mgr">
        <div className="tp-img-mgr__header">
          <h3 className="tp-img-mgr__title">
            Photos — {lodging?.lodging_name || 'Lodging'}
          </h3>
          <button className="tp-btn--ghost tp-img-mgr__close" onClick={onClose}>✕</button>
        </div>

        <div className="tp-img-mgr__toolbar">
          <div className="tp-img-mgr__toolbar-left">
            <label className="tp-img-mgr__checkbox-label">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={allSelected ? clearSelection : selectAll}
              />
              {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
            </label>
            {selected.size > 0 && (
              <button className="tp-btn--danger tp-btn--sm" onClick={deleteSelected}>
                Delete selected ({selected.size})
              </button>
            )}
          </div>
          <div className="tp-img-mgr__toolbar-right">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={e => handleFiles(Array.from(e.target.files || []))}
            />
            <button
              className="tp-btn--ghost tp-btn--sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : '+ Add Photos'}
            </button>
          </div>
        </div>

        <div
          className={`tp-img-mgr__body${fileDragOver ? ' tp-img-mgr__body--file-drop' : ''}`}
          onDragOver={onGridDragOver}
          onDragLeave={onGridDragLeave}
          onDrop={onGridDrop}
        >
          {fileDragOver && (
            <div className="tp-img-mgr__drop-overlay">
              Drop photos to add them
            </div>
          )}

          {images.length === 0 ? (
            <div className="tp-img-mgr__empty">
              No photos yet. Click <strong>+ Add Photos</strong> or drop images here.
            </div>
          ) : (
            <div className="tp-img-mgr__grid">
              {images.map((img, idx) => (
                <div
                  key={img.image_id}
                  className={[
                    'tp-img-mgr__item',
                    selected.has(img.image_id) ? 'tp-img-mgr__item--selected' : '',
                    dragOverId === img.image_id ? 'tp-img-mgr__item--drag-over' : '',
                  ].join(' ')}
                  draggable
                  onDragStart={e => onDragStart(e, img.image_id)}
                  onDragOver={e => onItemDragOver(e, img.image_id)}
                  onDrop={e => onItemDrop(e, img.image_id)}
                  onDragEnd={onDragEnd}
                >
                  <div className="tp-img-mgr__drag-handle" title="Drag to reorder">⠿</div>

                  <div className="tp-img-mgr__item-check">
                    <input
                      type="checkbox"
                      checked={selected.has(img.image_id)}
                      onChange={() => toggleSelect(img.image_id)}
                    />
                  </div>

                  <div className="tp-img-mgr__thumb-wrap">
                    <img
                      src={toAbsoluteUrl(img.media_url)}
                      alt=""
                      className="tp-img-mgr__thumb"
                      draggable={false}
                    />
                    <button
                      className="tp-img-mgr__delete-btn"
                      title="Remove photo"
                      onClick={(e) => { e.stopPropagation(); deleteSingle(img.image_id); }}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="tp-img-mgr__order-btns">
                    <button
                      className="tp-img-mgr__order-btn"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      title="Move left"
                    >‹</button>
                    <span className="tp-img-mgr__order-num">{idx + 1}</span>
                    <button
                      className="tp-img-mgr__order-btn"
                      onClick={() => move(idx, 1)}
                      disabled={idx === images.length - 1}
                      title="Move right"
                    >›</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="tp-img-mgr__footer">
          <span className="tp-img-mgr__hint">Drag photos to reorder, or drop files here to add.</span>
          <button className="tp-btn--ghost" onClick={onClose}>Done</button>
        </div>
      </div>
    </Modal>
  );
}
